import { Operation } from 'module/api/filter/types';
import { SourceType } from 'module/api/filter/types';
import {
  BaseFieldFilter,
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFieldFilter,
} from 'module/api/filter/types';
import { unreachable } from 'module/helpers/unreachable';
import { FieldType } from 'module/types/filter-field/field/field';
import {
  AssigneeField,
  BooleanField,
  DateField,
  EstimateField,
  FilterableField,
  MultiSelectField,
  MultiUserField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from 'module/types/filter-field/field/filterable-field';
import { AssigneeFieldFilter } from 'module/types/filter-field/filter/assignee';
import { BooleanFieldFilter } from 'module/types/filter-field/filter/boolean';
import { DateFieldFilter } from 'module/types/filter-field/filter/date';
import { EstimateFieldFilter } from 'module/types/filter-field/filter/estimate';
import { FieldFilter } from 'module/types/filter-field/filter/index';
import { MultiSelectFieldFilter } from 'module/types/filter-field/filter/multi-select';
import { MultiUserFieldFilter } from 'module/types/filter-field/filter/multi-user';
import { NumberFieldFilter } from 'module/types/filter-field/filter/number';
import { SingleSelectFieldFilter } from 'module/types/filter-field/filter/single-select';
import { SingleUserFieldFilter } from 'module/types/filter-field/filter/single-user';
import { TextFieldFilter } from 'module/types/filter-field/filter/text';
import { GroupCriteria } from 'module/types/filter-field/grouped-filter/filter';
import { isFieldFilterCriteria } from 'module/types/filter-field/grouped-filter/utils';
import { GroupSeparator } from 'module/types/filter-field/separators';
import { AssigneeFilter } from './fields/assignee';
import { BooleanFilter } from './fields/boolean';
import { DateFilter } from './fields/date';
import { EstimateFilter } from './fields/estimate';
import { MultiSelectFilter } from './fields/multi-select';
import { MultiUserFilter } from './fields/multi-user';
import { NumberFilter } from './fields/number';
import { SingleSelectFilter } from './fields/single-select';
import { SingleUserFilter } from './fields/single-user';
import { TextFilter } from './fields/text';

export const toApiFormat = (filter: FieldFilter): BaseFieldFilter => {
  switch (filter.field.type) {
    case FieldType.Boolean:
      return BooleanFilter.toApiFormat(filter as BooleanFieldFilter);
    case FieldType.Number:
      return NumberFilter.toApiFormat(filter as NumberFieldFilter);
    case FieldType.Date:
      return DateFilter.toApiFormat(filter as DateFieldFilter);
    case FieldType.Estimate:
      return EstimateFilter.toApiFormat(filter as EstimateFieldFilter);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFilter.toApiFormat(filter as TextFieldFilter);
    case FieldType.SingleSelect:
      return SingleSelectFilter.toApiFormat(filter as SingleSelectFieldFilter);
    case FieldType.MultiSelect:
      return MultiSelectFilter.toApiFormat(filter as MultiSelectFieldFilter);
    case FieldType.SingleUser:
      return SingleUserFilter.toApiFormat(filter as SingleUserFieldFilter);
    case FieldType.MultiUser:
      return MultiUserFilter.toApiFormat(filter as MultiUserFieldFilter);
    case FieldType.Assignee:
      return AssigneeFilter.toApiFormat(filter as AssigneeFieldFilter);
    default:
      return unreachable(filter.field);
  }
};

const parse = (
  filter: SimpleSearchFieldFilter,
  field: FilterableField,
): FieldFilter => {
  const fieldType = field.type;

  switch (fieldType) {
    case FieldType.Boolean:
      return BooleanFilter.parse(
        filter as NativeFieldFilter,
        field as BooleanField,
      );
    case FieldType.Number:
      return NumberFilter.parse(
        filter as NativeFieldFilter,
        field as NumberField,
      );
    case FieldType.Date:
      return DateFilter.parse(filter as NativeFieldFilter, field as DateField);
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return TextFilter.parse(filter as NativeFieldFilter, field as TextField);
    case FieldType.SingleSelect:
      return SingleSelectFilter.parse(
        filter as NativeFieldFilter,
        field as SingleSelectField,
      );
    case FieldType.MultiSelect:
      return MultiSelectFilter.parse(
        filter as NativeFieldFilter,
        field as MultiSelectField,
      );
    case FieldType.SingleUser:
      return SingleUserFilter.parse(
        filter as NativeFieldFilter,
        field as SingleUserField,
      );
    case FieldType.MultiUser:
      return MultiUserFilter.parse(
        filter as NativeFieldFilter,
        field as MultiUserField,
      );
    case FieldType.Estimate:
      return EstimateFilter.parse(
        filter as NativeFieldFilter,
        field as EstimateField,
      );
    case FieldType.Assignee:
      return AssigneeFilter.parse(
        filter as NativeFieldFilter,
        field as AssigneeField,
      );
    default:
      return unreachable(fieldType);
  }
};

const toNativeFieldFilterFormat = (filter: FieldFilter): NativeFieldFilter => {
  return {
    name: filter.field.fieldName,
    ...toApiFormat(filter),
  };
};

const toCustomFieldFilterFormat = (filter: FieldFilter): CustomFieldFilter => {
  return {
    fieldId: filter.field.fieldId,
    ...toApiFormat(filter),
  };
};

const getValueForQueryString = (
  filter: NativeFieldFilter | CustomFieldFilter,
) => {
  switch (filter.operation) {
    case Operation.IsEmpty:
    case Operation.IsNotEmpty:
    case Operation.IsNull:
      return true;
    default:
      return filter.value ? encodeURIComponent(filter.value) : undefined;
  }
};

const nativeFieldToQueryStringFormat = (field: NativeFieldFilter): string =>
  `${field.key ? `${field.key}${GroupSeparator}` : ''}${
    field.sourceType
  }${GroupSeparator}${field.name}.${field.operation}=${getValueForQueryString(
    field,
  )}`;

export const customFieldKeyToString = (field: {
  sourceType: SourceType;
  fieldId: string | number;
  key?: string;
}): string =>
  `${field.key ? `${field.key}${GroupSeparator}` : ''}${
    field.sourceType
  }${GroupSeparator}field.${field.fieldId}`;

const customFieldToQueryStringFormat = (field: CustomFieldFilter): string =>
  `${customFieldKeyToString(field)}.${field.operation}=${getValueForQueryString(
    field,
  )}`;

const toQueryStringFormat = (filters: FieldFilter[]): string =>
  [
    ...filters
      .filter((x) => !x.field.userCreated)
      .map(toNativeFieldFilterFormat)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      .filter((x) => x.field.userCreated)
      .map(toCustomFieldFilterFormat)
      .map(customFieldToQueryStringFormat),
  ].join('&');

const convertQueryStringToObject = (
  filterString: string,
): { [key: string]: string } => {
  const filterObject = {};

  filterString.split('&').forEach((x) => {
    const [key, value] = decodeURIComponent(x).split('=');

    if (Object.keys(filterObject).includes(key)) {
      if (!Array.isArray(filterObject[key])) {
        filterObject[key] = [filterObject[key], value];
      } else {
        filterObject[key] = [...filterObject[key], value];
      }
    } else {
      filterObject[key] = value;
    }
  });

  return filterObject;
};

export const ApiFilter = {
  toNativeFieldFilterFormat,
  toCustomFieldFilterFormat,
  parse,
  toQueryStringFormat,
  nativeFieldToQueryStringFormat,
  customFieldToQueryStringFormat,
  convertQueryStringToObject,
};

const toNativeFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): NativeFieldFilter | undefined => {
  if (isFieldFilterCriteria(filter.criteria)) {
    return {
      name: filter.criteria.field.fieldName,
      ...(filter.key
        ? {
            key: filter.key,
          }
        : {}),
      ...toApiFormat(filter.criteria),
    };
  }
};

const toCustomFieldFilterFormatForGroups = (
  filter: GroupCriteria,
): CustomFieldFilter | undefined => {
  if (isFieldFilterCriteria(filter.criteria)) {
    return {
      fieldId: filter.criteria.field.fieldId,
      ...(filter.key
        ? {
            key: filter.key,
          }
        : {}),
      ...toApiFormat(filter.criteria),
    };
  }
};

const toQueryStringFormatForGroups = (filters: GroupCriteria[]): string =>
  [
    ...filters
      .filter(
        (x) =>
          isFieldFilterCriteria(x.criteria) && !x.criteria.field.userCreated,
      )
      .map(toNativeFieldFilterFormatForGroups)
      .filter((x) => x != null)
      .map(nativeFieldToQueryStringFormat),
    ...filters
      .filter(
        (x) =>
          isFieldFilterCriteria(x.criteria) && x.criteria.field.userCreated,
      )
      .map(toCustomFieldFilterFormatForGroups)
      .filter((x) => x != null)
      .map(customFieldToQueryStringFormat),
  ].join('&');

export const ApiGroupFilter = {
  toNativeFieldFilterFormatForGroups,
  toCustomFieldFilterFormatForGroups,
  toQueryStringFormatForGroups,
};
